import type { ReactNode } from 'react';

export interface AppStateAction {
  label: string;
  onClick: () => void;
  /** Primary = filled blue button; secondary = outline */
  variant?: 'primary' | 'secondary';
  ariaLabel?: string;
}

interface AppStatePageProps {
  statusCode?: string;
  statusColor?: string;
  icon?: ReactNode;
  title: string;
  message: string;
  actions?: AppStateAction[];
}

const DEFAULT_ICON = (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--rb-color-text-dark-secondary, rgba(0,15,30,0.6))"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export default function AppStatePage({
  statusCode,
  statusColor = 'var(--rb-color-text-dark-secondary, rgba(0,15,30,0.6))',
  icon = DEFAULT_ICON,
  title,
  message,
  actions = [],
}: AppStatePageProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        padding: 'var(--rb-x-loose)',
        textAlign: 'center',
        gap: 'var(--rb-loose)',
      }}
    >
      {icon}

      <div style={{ maxWidth: 520 }}>
        {statusCode ? (
          <p
            style={{
              fontFamily: 'var(--rb-font-body, "Bull Text", sans-serif)',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: statusColor,
              margin: '0 0 var(--rb-x-tight)',
            }}
          >
            {statusCode}
          </p>
        ) : null}
        <h1
          style={{
            fontFamily: 'var(--rb-font-headline, "Bull", sans-serif)',
            fontWeight: 700,
            fontSize: 32,
            color: 'var(--rb-color-text-dark-primary, #000F1E)',
            margin: '0 0 var(--rb-normal)',
            lineHeight: 1.2,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontFamily: 'var(--rb-font-body, "Bull Text", sans-serif)',
            fontSize: 16,
            color: 'var(--rb-color-text-dark-secondary, rgba(0,15,30,0.6))',
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          {message}
        </p>
      </div>

      {actions.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--rb-tight)',
            justifyContent: 'center',
          }}
        >
          {actions.map((action) => {
            const isPrimary = action.variant !== 'secondary';
            return (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                aria-label={action.ariaLabel ?? action.label}
                style={{
                  background: isPrimary
                    ? 'var(--rb-color-primary, #1B6AEE)'
                    : 'transparent',
                  color: isPrimary
                    ? 'var(--rb-color-text-light-primary, #fff)'
                    : 'var(--rb-color-primary, #1B6AEE)',
                  border: isPrimary
                    ? 'none'
                    : '1px solid var(--rb-color-primary, #1B6AEE)',
                  borderRadius: 'var(--rb-border-radius-full, 999px)',
                  padding: '10px 28px',
                  fontFamily: 'var(--rb-font-body, "Bull Text", sans-serif)',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (isPrimary) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--rb-color-primary-lighter, #3279F0)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isPrimary) {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--rb-color-primary, #1B6AEE)';
                  }
                }}
              >
                {action.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
